import React, { useMemo } from 'react';
import { List } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountData } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { FilterMenu } from '../../common/filter/FilterMenu';
import { CheckboxGroupSection } from '../../common/filter/CheckboxGroupSection';
import { getActiveLocations } from '../../common/filter/locationGrouping';
import { useLanguage } from '../../common/GeneralUtils';
import { usePassesFilter } from './PassesFilterContext';
import { fromSentinel, toSentinel, UNDETERMINED_LOCATION_SENTINEL } from './locationFilterItems';

export const AtRiskLocationFilter = () => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { atRiskLocationIds, setAtRiskLocationIds, isAtRiskFilterActive, resetAtRiskFilter } = usePassesFilter();

  const activeLocations = useMemo(() => getActiveLocations(account), [account]);

  if (activeLocations.length <= 1) return null;

  const items: string[] = [...activeLocations.map((l) => l.id), UNDETERMINED_LOCATION_SENTINEL];
  const selected: string[] = atRiskLocationIds === null ? items : atRiskLocationIds.map(toSentinel);

  const itemLabel = (id: string): string => {
    if (id === UNDETERMINED_LOCATION_SENTINEL) return t('passes.filter.location_undetermined');
    const loc = activeLocations.find((l) => l.id === id);
    return loc?.name?.[language] || loc?.name?.en || t('passes.filter.location_unnamed');
  };

  return (
    <FilterMenu
      name="AtRiskPasses"
      isActive={isAtRiskFilterActive}
      summaryLabel={
        atRiskLocationIds === null
          ? t('passes.filter.label')
          : t('passes.filter.summary.locations', { count: atRiskLocationIds.length, total: items.length })
      }
      onReset={resetAtRiskFilter}>
      <List disablePadding>
        <CheckboxGroupSection
          title={t('passes.filter.location')}
          items={items}
          itemLabel={itemLabel}
          selected={selected}
          onChange={(next) => setAtRiskLocationIds(next.length === items.length ? null : next.map(fromSentinel))}
          withDivider={false}
        />
      </List>
    </FilterMenu>
  );
};
